import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useCallback } from 'react';
import { AuthQueryOptions, VERIFICATION_TYPE } from '@/api';
import { ROUTES } from '@/constants';
import { useCustomRouter } from './useCustomRouter';
interface UseVerificationCheckOptions {
  /**
   * 쿼리 활성화 여부
   * @default true
   */
  enabled?: boolean;
  /**
   * 본인인증 페이지 리다이렉트 후 돌아올 경로
   * @default 현재 pathname
   */
  redirectPath?: string;
}

/**
 * 사용자의 본인인증 상태를 확인하고, 본인인증이 필요한 경우 본인인증 페이지로 리다이렉트하는 함수를 제공하는 훅
 *
 * @example
 * ```tsx
 * const { checkVerification, isCi } = useVerificationCheck();
 *
 * const handleClick = () => {
 *   // 본인인증이 안되어 있으면 본인인증 페이지로 리다이렉트하고 true 반환
 *   // 본인인증이 되어있으면 false 반환
 *   if (checkVerification()) {
 *     return; // 본인인증 페이지로 이동했으므로 종료
 *   }
 *
 *   // 본인인증이 되어있으면 다음 로직 실행
 *   doSomething();
 * };
 * ```
 */
export const useVerificationCheck = t0 => {
  const $ = _c(14);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    enabled: t2,
    redirectPath
  } = options;
  const enabled = t2 === undefined ? true : t2;
  const pathname = usePathname();
  const router = useCustomRouter();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = AuthQueryOptions["post:/wello/v2/auth/member-verification/info"]();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== enabled) {
    t4 = {
      ...t3,
      enabled
    };
    $[3] = enabled;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const verificationInfoQuery = useQuery(t4);
  const isCi = !!verificationInfoQuery.data?.context?.ci;
  let t5;
  if ($[5] !== isCi || $[6] !== pathname || $[7] !== redirectPath || $[8] !== router) {
    t5 = () => {
      if (!isCi) {
        router.push(ROUTES.EASY_CERTIFICATION.withSearchParams({
          searchParams: {
            verification: VERIFICATION_TYPE.PROFILE_UPDATE,
            redirect: redirectPath ?? pathname ?? ""
          }
        }));
        return true;
      }
      return false;
    };
    $[5] = isCi;
    $[6] = pathname;
    $[7] = redirectPath;
    $[8] = router;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const checkVerification = t5;
  let t6;
  if ($[10] !== checkVerification || $[11] !== isCi || $[12] !== verificationInfoQuery.isLoading) {
    t6 = {
      checkVerification,
      isCi,
      isLoading: verificationInfoQuery.isLoading
    };
    $[10] = checkVerification;
    $[11] = isCi;
    $[12] = verificationInfoQuery.isLoading;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};